// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldSelect, FieldText, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Utils
import { voltageLevelTypes } from "utils/constants/index";

/* Schema-driven technical-details form. Each entry: API detail key + the
   i18n label id under resources:new_resource.form.<labelKey>.label.
   `input: "select"` entries render a FieldSelect with `options`. */
export const DETAIL_FIELDS = {
  SCPU: [{ key: "generation_capacity", labelKey: "generationCapacity" }],
  STORAGE: [
    { key: "nominal_capacity", labelKey: "nominalCapacity" },
    { key: "max_power", labelKey: "maxPower" },
    { key: "charging_efficiency", labelKey: "chargingEfficiency" },
    { key: "discharging_efficiency", labelKey: "dischargingEfficiency" },
    { key: "min_SOC", labelKey: "minSOC" },
    { key: "max_SOC", labelKey: "maxSOC" },
    { key: "deg_cost", labelKey: "degCost" },
    { key: "init_soc", labelKey: "initSoc" },
  ],
  EV: [
    { key: "nominal_capacity", labelKey: "nominalCapacity" },
    { key: "max_power_input", labelKey: "maxPowerInput" },
    { key: "max_power_output", labelKey: "maxPowerOutput" },
    { key: "charging_efficiency", labelKey: "chargingEfficiency" },
    { key: "discharging_efficiency", labelKey: "dischargingEfficiency" },
    { key: "min_SOC", labelKey: "minSOC" },
    { key: "max_SOC", labelKey: "maxSOC" },
    { key: "desired_min_SOC", labelKey: "desiredMinSOC" },
    { key: "deg_cost", labelKey: "degCost" },
    { key: "init_soc", labelKey: "initSoc" },
  ],
  EWH: [
    { key: "capacity", labelKey: "capacity" },
    { key: "power", labelKey: "power" },
    { key: "max_temp", labelKey: "maxTemp" },
    { key: "default_start_temp", labelKey: "defaultStartTemp" },
    { key: "target_temp", labelKey: "targetTemp" },
  ],
  HP: [
    { key: "power_rated", labelKey: "powerRated" },
    { key: "capacity_tank", labelKey: "capacityTank" },
    { key: "temp_inlet", labelKey: "tempInlet" },
    { key: "temp_desired", labelKey: "tempDesired" },
    { key: "temp_out_init", labelKey: "tempOutInit" },
    { key: "temp_indoor_init", labelKey: "tempIndoorInit" },
    { key: "temp_indoor_final", labelKey: "tempIndoorFinal" },
    { key: "temp_indoor_min", labelKey: "tempIndoorMin" },
    { key: "temp_indoor_max", labelKey: "tempIndoorMax" },
    { key: "temp_out_min", labelKey: "tempOutMin" },
    { key: "temp_out_max", labelKey: "tempOutMax" },
    { key: "u_value", labelKey: "uValue" },
    { key: "thermal_resistance", labelKey: "thermalResistance" },
    { key: "h_rad", labelKey: "hRad" },
    { key: "area_rad", labelKey: "areaRad" },
  ],
  IC: [
    { key: "sum_certified_power", labelKey: "sumCertifiedPower" },
    {
      key: "ic_voltage_level",
      labelKey: "powerVoltageLevel",
      input: "select",
      options: voltageLevelTypes,
    },
    { key: "description", labelKey: "description", input: "text" },
  ],
};
DETAIL_FIELDS.IU = DETAIL_FIELDS.IC;

function DetailsInfo({ data, onUpdate, isLoading, readOnly }) {
  const { t } = useTranslation(["resources"]);
  const fields = DETAIL_FIELDS[data.type] || [];
  const details = data.details || {};

  const initial = fields.reduce((acc, f) => {
    acc[f.key] = details[f.key] ?? (f.input ? "" : 0);
    return acc;
  }, {});

  const [original, setOriginal] = useState(initial);
  const [values, setValues] = useState(initial);

  const canSave = !isLoading && fields.some((f) => original[f.key] !== values[f.key]);

  const onUpdateClicked = async () => {
    if (!canSave) return;
    setOriginal(values);
    const success = await onUpdate({ type: data.type, details: { ...values } });
    if (!success) {
      setOriginal(initial);
    }
  };

  if (fields.length === 0) return null;

  return (
    <SectionCard
      id="details"
      icon="tune"
      title={t("single.details.title")}
      action={
        readOnly ? null : (
          <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
            <Icon name="save" />
            {t("single.details.update")}
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map((f) =>
          f.input === "select" ? (
            <FieldSelect
              key={f.key}
              label={t(`new_resource.form.${f.labelKey}.label`)}
              value={values[f.key]}
              options={f.options}
              onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
              disabled={readOnly}
            />
          ) : (
            <FieldText
              key={f.key}
              label={t(`new_resource.form.${f.labelKey}.label`)}
              type={f.input === "text" ? "text" : "number"}
              value={values[f.key]}
              onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
              disabled={readOnly}
            />
          )
        )}
      </div>
    </SectionCard>
  );
}

DetailsInfo.defaultProps = {
  isLoading: false,
  onUpdate: async () => false,
  readOnly: false,
};

DetailsInfo.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    details: PropTypes.object,
  }).isRequired,
  onUpdate: PropTypes.func,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default DetailsInfo;
